"use client";

import Link from "next/link";
import { ChevronIcon } from "../../components/ui/icons";
import styles from "./Pagination.module.css";

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  locale: string;
};

export default function Pagination({
  currentPage,
  totalPages,
  locale,
}: PaginationProps) {
  if (totalPages <= 1) return null;
  
  // Calculate the base URL based on search parameters
  const baseUrl = `/${locale}/blog`;

  // Generate an array of page numbers to display
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // If total pages is less than max, show all pages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate start and end of middle pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(currentPage + 1, totalPages - 1);
      
      // Adjust if we're near the start
      if (currentPage <= 3) {
        endPage = Math.min(maxPagesToShow - 1, totalPages - 1);
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = Math.max(2, totalPages - maxPagesToShow + 2);
      }
      
      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      // Always show last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  const pageNumbers = getPageNumbers();

  return (
    <div className={styles.wrapper}>
      <nav className={styles.nav} aria-label="Pagination">
        {/* Previous page button */}
        {currentPage > 1 ? (
          <Link
            href={`${baseUrl}?page=${currentPage - 1}`}
            className={styles.prevButton}
            aria-label="Previous page"
          >
            <ChevronIcon className={styles.icon} direction="left" />
          </Link>
        ) : (
          <span
            className={styles.prevButtonDisabled}
          >
            <ChevronIcon className={styles.icon} direction="left" />
          </span>
        )}
        
        {/* Page number buttons */}
        {pageNumbers.map((page, index) => (
          page === '...' ? (
            <span 
              key={`ellipsis-${index}`} 
              className={styles.ellipsis}
            >
              ...
            </span>
          ) : (
            <Link
              key={`page-${page}`}
              href={`${baseUrl}?page=${page}`}
              className={`${styles.pageButton} ${currentPage === page ? styles.active : ''}`}
              aria-current={currentPage === page ? "page" : undefined}
            >
              {page}
            </Link>
          )
        ))}
        
        {/* Next page button */}
        {currentPage < totalPages ? (
          <Link
            href={`${baseUrl}?page=${currentPage + 1}`}
            className={styles.nextButton}
            aria-label="Next page"
          >
            <ChevronIcon className={styles.icon} direction="right" />
          </Link>
        ) : (
          <span
            className={styles.nextButtonDisabled}
          >
            <ChevronIcon className={styles.icon} direction="right" />
          </span>
        )}
      </nav>
    </div>
  );
} 